import React from "react";
import { 
  Droplets, 
  Gauge, 
  ShieldCheck, 
  Leaf, 
  Clock, 
  DollarSign 
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface BenefitCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function BenefitCard({ icon, title, description }: BenefitCardProps) {
  return (
    <div className="flex flex-col gap-4 p-6 rounded-lg border bg-card text-card-foreground shadow-sm hover:shadow-md transition-shadow">
      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
        {icon}
      </div>
      <div className="space-y-2">
        <h3 className="text-xl font-medium tracking-tight">{title}</h3>
        <p className="text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

interface FuelOxBenefitsProps {
  title?: string;
  description?: string;
}

function FuelOxBenefits({
  title = "FuelOx Marine Benefits",
  description = "Our advanced fuel additive technology delivers multiple advantages for marine vessels, improving performance while reducing environmental impact."
}: FuelOxBenefitsProps) {
  const benefits: BenefitCardProps[] = [
    {
      icon: <Gauge className="h-6 w-6" />,
      title: "Improved Fuel Efficiency",
      description: "Increases fuel economy by up to 15%, reducing overall fuel consumption and operating costs."
    },
    {
      icon: <Leaf className="h-6 w-6" />,
      title: "Reduced Emissions",
      description: "Lowers harmful exhaust emissions including CO2, NOx, and particulate matter for cleaner operation."
    },
    {
      icon: <ShieldCheck className="h-6 w-6" />,
      title: "Engine Protection",
      description: "Prevents carbon buildup and corrosion, keeping engine components clean and functioning optimally."
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: "Extended Engine Life",
      description: "Reduces wear and tear on critical engine components, extending the operational lifespan of your vessel."
    },
    {
      icon: <Droplets className="h-6 w-6" />,
      title: "Water Separation",
      description: "Effectively separates water from fuel, preventing microbial growth and fuel system damage."
    },
    {
      icon: <DollarSign className="h-6 w-6" />,
      title: "Cost Savings",
      description: "Reduces maintenance costs and downtime while extending intervals between service requirements."
    }
  ];

  return (
    <div id="benefits" className="w-full py-20 lg:py-32 bg-background">
      <div className="container mx-auto">
        <div className="flex flex-col gap-12">
          <div className="flex gap-4 flex-col items-center text-center">
            <div>
              <Badge>Marine Technology</Badge>
            </div>
            <div className="flex gap-3 flex-col max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-5xl tracking-tighter font-bold">
                {title}
              </h2>
              <p className="text-lg leading-relaxed tracking-tight text-muted-foreground">
                {description}
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <BenefitCard
                key={index}
                icon={benefit.icon}
                title={benefit.title}
                description={benefit.description}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FuelOxBenefitsDemo() {
  return (
    <div className="w-full">
      <FuelOxBenefits />
    </div>
  );
}

export default FuelOxBenefitsDemo;
